import * as Api from '@ViewModels';
import { MutationScope, useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { ThemeColors } from '../../web/components-v2/Theme/theme';

const BASE_URL_PATH = 'scheduler/category';

export interface MeetingCategoryFields {
	name: string;
	color?: keyof ThemeColors['pill'];
	maxPerDay?: number;
	maxPerMonth?: number;
}

interface MutationOptions {
	onSuccess?: (data: Api.IMeetingCategory) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
	scope?: MutationScope;
}

function composeUrl({
	path,
	forUserId,
	impersonationContext,
}: {
	path: string;
	forUserId?: string;
	impersonationContext?: Api.IImpersonationContext;
}) {
	return Api.ImpersonationBroker.composeApiUrl({
		impersonationContext,
		urlPath: path,
		queryParams: forUserId ? { forUserId } : undefined,
	});
}

export function useMeetingCategoryCreateMutation({ onSuccess, onError, scope }: MutationOptions) {
	const userSession = useUserSession();

	return useMutation({
		scope,
		mutationFn: ({
			category,
			forUserId,
			impersonationContext,
		}: {
			category: MeetingCategoryFields;
			forUserId?: string;
			impersonationContext?: Api.IImpersonationContext;
		}) =>
			userSession.webServiceHelper.callAsync<Api.IMeetingCategory>(
				composeUrl({ path: BASE_URL_PATH, forUserId, impersonationContext }),
				'POST',
				category
			),
		onSuccess,
		onError,
	});
}

export function useMeetingCategoryUpdateMutation({ onSuccess, onError, scope }: MutationOptions) {
	const userSession = useUserSession();

	return useMutation({
		scope,
		mutationFn: ({
			categoryId,
			category,
			forUserId,
			impersonationContext,
		}: {
			categoryId: string;
			category: MeetingCategoryFields;
			forUserId?: string;
			impersonationContext?: Api.IImpersonationContext;
		}) =>
			userSession.webServiceHelper.callAsync<Api.IMeetingCategory>(
				composeUrl({ path: `${BASE_URL_PATH}/${categoryId}`, forUserId, impersonationContext }),
				'PUT',
				category
			),
		onSuccess,
		onError,
	});
}

export function useMeetingCategoryDeleteMutation({ onSuccess, onError, scope }: MutationOptions) {
	const userSession = useUserSession();

	return useMutation({
		scope,
		mutationFn: ({
			categoryId,
			forUserId,
			impersonationContext,
		}: {
			categoryId: string;
			forUserId?: string;
			impersonationContext?: Api.IImpersonationContext;
		}) =>
			userSession.webServiceHelper.callAsync<Api.IMeetingCategory>(
				composeUrl({ path: `${BASE_URL_PATH}/${categoryId}`, forUserId, impersonationContext }),
				'DELETE'
			),
		onSuccess,
		onError,
	});
}
